import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { MapPin, Stethoscope, ShieldCheck, X, ArrowRight, ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LocationAutocomplete } from "@/components/forms/LocationAutocomplete";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import { InsurancePickerDialog } from "@/components/forms/InsurancePickerDialog";
import { cityFromLocation, stateAbbr } from "@/lib/location-autocomplete";
import { specialtySlug, toSlug, stateFromNameSlug, stateNameSlug } from "@/lib/seo-slugs";

export function HeroSearch() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [specialty, setSpecialty] = useState("");
  const [location, setLocation] = useState("");
  // Carrier or plan token ("aetna", "aetna:choice-pos-ii", "selfpay") plus the
  // name to show for it. The dialog hands back both, so the hero never has to
  // load the 3.4k-row catalog just to label the field.
  const [insurance, setInsurance] = useState<{ token: string; label: string } | null>(null);
  const [insuranceOpen, setInsuranceOpen] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Single-dimension picks get the clean /providers/... URL; combining
    // specialty + city has no clean URL yet, so those fall back to /search's
    // own query params. Insurance rides along as a search param on all three
    // routes — every one of them accepts it in validateSearch.
    const insuranceSearch = insurance ? { insurance: insurance.token } : {};
    if (specialty && !location) {
      navigate({
        to: "/providers/$id",
        params: { id: specialtySlug(specialty) },
        search: insuranceSearch as any,
      });
      return;
    }
    if (location && !specialty && location.includes(",")) {
      const city = cityFromLocation(location);
      const state = stateFromNameSlug(stateAbbr(location.split(",")[1]));
      if (city && state) {
        navigate({
          to: "/providers/$state/$city",
          params: { state: stateNameSlug(state.name), city: toSlug(city) },
          search: { page: undefined, ...insuranceSearch } as any,
        });
        return;
      }
    }
    const params: Record<string, string> = {};
    if (specialty) params.specialty = specialty;
    if (location) params.location = location;
    if (insurance) params.insurance = insurance.token;
    navigate({ to: "/search", search: params as any });
  };

  return (
    // Widened from 680px: a third field turned "Select a specialty" into
    // "Select a speci…" and truncated placeholders read as broken.
    <div className="mx-auto mt-10 w-full max-w-[880px]">
      <form
        onSubmit={submit}
        className="flex flex-col sm:flex-row items-stretch gap-2 card-surface-flat p-2 rounded-md"
        role="search"
        aria-label="Find a provider"
      >
        <div className="relative flex-1 min-w-0">
          <SpecialtySelect
            value={specialty}
            onChange={setSpecialty}
            placeholder={t.search.selectSpecialty || "Select a specialty"}
            allowCustom={false}
            icon={Stethoscope}
            className="h-11 w-full border border-transparent focus-visible:border-border bg-background px-3 rounded-md"
          />
        </div>
        
        <div className="relative flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-1 min-w-0 px-3 h-11 border border-transparent focus-within:border-border bg-background rounded-md">
            <MapPin className="h-4 w-4 text-primary shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <LocationAutocomplete
                value={location}
                onChange={setLocation}
                placeholder={t.search.locationPh || "City, ZIP, or near me"}
                inputClassName="border-0 shadow-none focus-visible:ring-0 px-0 h-9 bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none w-full"
              />
            </div>
            {location && (
              <button
                type="button"
                onClick={() => setLocation("")}
                className="shrink-0 p-1 text-muted-foreground hover:text-foreground cursor-pointer"
                aria-label="Clear location"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Same picker the search page uses, so a carrier chosen here means
            exactly what it means once the results load. */}
        <div className="relative flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setInsuranceOpen(true)}
            className="flex items-center gap-2 w-full h-11 px-3 border border-transparent hover:border-border focus-visible:border-border bg-background rounded-md text-[14px] cursor-pointer outline-none"
          >
            <ShieldCheck className="h-4 w-4 text-primary shrink-0" aria-hidden />
            <span className={`truncate text-left flex-1 min-w-0 ${insurance ? "text-foreground" : "text-muted-foreground"}`}>
              {insurance?.label ?? t.search.selectInsurance ?? "Insurance"}
            </span>
            {insurance ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setInsurance(null); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setInsurance(null); } }}
                className="shrink-0 p-1 text-muted-foreground hover:text-foreground"
                aria-label="Clear insurance"
              >
                <X className="h-4 w-4" />
              </span>
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
            )}
          </button>
        </div>
        <InsurancePickerDialog
          open={insuranceOpen}
          onOpenChange={setInsuranceOpen}
          value={insurance?.token ?? null}
          onSelect={(token, label) => setInsurance(token ? { token, label: label ?? token } : null)}
        />

        <button
          type="submit"
          className="group inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md shrink-0 cursor-pointer"
        >
          <span>{t.search.submit || "Search"}</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
        </button>
      </form>
    </div>
  );
}


